import {Dropdown, DropdownItem, Tooltip} from 'platform/components';
import {Link} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetSalePresetSettingsListQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery, usePatchSaleVehicleSalePresetMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {noop, suffixTestId, TestIdProps} from 'shared/web';

import {useAdPlatformsVehicleUpdateDialog} from '../../../hooks/useAdPlatformsVehicleUpdateDialog';
import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../../utils/handleApiError';
import {NoPermissionTooltip} from '../../NoPermissionTooltip/NoPermissionTooltip';
import {openSalePresetUpdateInfoDialog} from '../../SalePresetUpdateInfoDialog/openSalePresetUpdateInfoDialog';

export interface SalePresetProps extends TestIdProps {
  recordId: string;
}

export function SalePreset(props: SalePresetProps) {
  const {data: salePresetList, isLoading: isSalePresetListQueryLoading} =
    useGetSalePresetSettingsListQuery();
  const {data: saleVehicle, isLoading: isSaleVehicleQueryLoading} = useGetSaleVehicleQuery({
    vehicleId: props.recordId,
  });
  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.recordId,
    resourceId: EntityResourceIds.vehicle,
  });
  const [patchSalePreset, {isLoading: isPatchSalePresetMutationLoading}] =
    usePatchSaleVehicleSalePresetMutation();
  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();

  const [canUpdateVehicle, canEditGeneralSettings] = usePermissions({
    permissionKeys: ['updateVehicle', 'editGeneralSettings'],
    scopes: {
      updateVehicle: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      editGeneralSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
    },
  });

  const canEditSalePreset = canUpdateVehicle && canEditGeneralSettings;

  const isLoading =
    isSalePresetListQueryLoading || isSaleVehicleQueryLoading || isPatchSalePresetMutationLoading;

  const selectedSalePreset = salePresetList?.find(
    (salePreset) => salePreset.id === saleVehicle?.salePresetId
  );

  const title = selectedSalePreset?.name ?? i18n.t('entity.vehicle.labels.salePreset');

  const updateSalePreset = (salePresetIdToUpdate: string) => {
    const previousSalePresetId = selectedSalePreset?.id;
    const hasSalePresetChanged = previousSalePresetId !== salePresetIdToUpdate;

    // A changed sale preset changes what the ads show (watermark, certificate), so the user is
    // offered an ads update, after the info dialog when a previous preset was replaced.
    const handleSalePresetUpdated = () =>
      match({hasSalePresetChanged, hadSalePreset: isNotNil(previousSalePresetId)})
        .with({hasSalePresetChanged: true, hadSalePreset: true}, () =>
          openSalePresetUpdateInfoDialog({
            salePresetId: salePresetIdToUpdate,
            onCloseComplete: () => updateVehicleAds(props.recordId),
          })
        )
        .with({hasSalePresetChanged: true, hadSalePreset: false}, () =>
          updateVehicleAds(props.recordId)
        )
        .otherwise(noop);

    patchSalePreset({
      vehicleId: props.recordId,
      patchSaleVehicleSalePresetRequestBody: {
        salePresetId: salePresetIdToUpdate,
      },
    })
      .unwrap()
      .then(handleSalePresetUpdated)
      .catch(handleApiError);
  };

  return canEditSalePreset ? (
    <Dropdown
      dropdownControl={
        <Tooltip
          label={i18n.t('entity.controls.labels.sale_preset.tooltip')}
          childrenWrapperDisplay="flex"
        >
          <Link
            size="small"
            isDisabled={!salePresetList?.length || isLoading || !canEditSalePreset}
            leftIcon="toggle/star"
            title={title}
            data-testid={suffixTestId('salePreset', props)}
          />
        </Tooltip>
      }
      isDisabled={isLoading || !canEditSalePreset}
    >
      {salePresetList?.map((salePreset) => (
        <DropdownItem
          key={salePreset.id}
          label={salePreset.name}
          onClick={() => {
            updateSalePreset(salePreset.id);
          }}
          data-testid={suffixTestId(`salePreset-${salePreset.id}`, props)}
        />
      ))}
    </Dropdown>
  ) : (
    <NoPermissionTooltip shouldShowTooltip>
      <Link
        size="small"
        isDisabled
        leftIcon="toggle/star"
        title={title}
        data-testid={suffixTestId('salePreset', props)}
      />
    </NoPermissionTooltip>
  );
}
